import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { describeCronExpression } from '../utils/cronHumanize'

export type CronType =
  | 'every-minutes'
  | 'every-hours'
  | 'daily'
  | 'weekdays'
  | 'weekends'
  | 'days'
  | 'day-of-month'
  | 'custom'

interface CronModel {
  type: CronType
  intervalN: number
  times: string[]
  dayOfWeek: string[]
  dayOfMonth: number
  raw: string
}

const DAYS: { value: string; label: string }[] = [
  { value: 'MON', label: 'Mon' },
  { value: 'TUE', label: 'Tue' },
  { value: 'WED', label: 'Wed' },
  { value: 'THU', label: 'Thu' },
  { value: 'FRI', label: 'Fri' },
  { value: 'SAT', label: 'Sat' },
  { value: 'SUN', label: 'Sun' },
]

const TYPE_OPTIONS: { value: CronType; label: string }[] = [
  { value: 'every-minutes', label: 'Every N minutes' },
  { value: 'every-hours', label: 'Every N hours' },
  { value: 'daily', label: 'At specific time(s) every day' },
  { value: 'weekdays', label: 'At specific time(s) on weekdays (Mon–Fri)' },
  { value: 'weekends', label: 'At specific time(s) on weekends (Sat & Sun)' },
  { value: 'days', label: 'At specific time(s) on selected days' },
  { value: 'day-of-month', label: 'At specific time(s) on day-of-month' },
  { value: 'custom', label: 'Custom (raw cron expression)' },
]

const HHMM = /^(\d{1,2}):(\d{1,2})$/

const emptyModel: CronModel = {
  type: 'custom',
  intervalN: 1,
  times: [],
  dayOfWeek: [],
  dayOfMonth: 1,
  raw: '',
}

function pad2(n: number | string): string {
  const s = String(n)
  return s.length < 2 ? `0${s}` : s
}

function normalizeTimes(input: string): { times: string[]; valid: boolean } {
  const parts = input
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean)
  if (parts.length === 0) return { times: [], valid: false }
  const out: string[] = []
  for (const p of parts) {
    const m = HHMM.exec(p)
    if (!m) return { times: parts, valid: false }
    const hh = parseInt(m[1], 10)
    const mm = parseInt(m[2], 10)
    if (hh < 0 || hh > 23 || mm < 0 || mm > 59) return { times: parts, valid: false }
    out.push(`${pad2(hh)}:${pad2(mm)}`)
  }
  return { times: out, valid: true }
}

function timesToCronParts(times: string[]): { sec: string; min: string; hour: string } | null {
  if (times.length === 0) return null
  const minutes = new Set(times.map((t) => t.split(':')[1]))
  if (minutes.size !== 1) return null // can't express mixed minutes in one Spring expression
  const min = String(parseInt([...minutes][0], 10))
  const hourList = [...new Set(times.map((t) => String(parseInt(t.split(':')[0], 10))))]
    .sort((a, b) => parseInt(a, 10) - parseInt(b, 10))
    .join(',')
  return { sec: '0', min, hour: hourList }
}

/** Build a Spring cron string from the structured model, or `null` if invalid. */
function buildCron(m: CronModel): string | null {
  switch (m.type) {
    case 'every-minutes': {
      const n = Math.max(1, Math.min(59, Math.floor(m.intervalN || 0)))
      return `0 */${n} * * * *`
    }
    case 'every-hours': {
      const n = Math.max(1, Math.min(23, Math.floor(m.intervalN || 0)))
      return `0 0 */${n} * * *`
    }
    case 'daily': {
      const p = timesToCronParts(m.times)
      if (!p) return null
      return `${p.sec} ${p.min} ${p.hour} * * *`
    }
    case 'weekdays': {
      const p = timesToCronParts(m.times)
      if (!p) return null
      return `${p.sec} ${p.min} ${p.hour} * * MON-FRI`
    }
    case 'weekends': {
      const p = timesToCronParts(m.times)
      if (!p) return null
      return `${p.sec} ${p.min} ${p.hour} * * SAT,SUN`
    }
    case 'days': {
      const p = timesToCronParts(m.times)
      if (!p || m.dayOfWeek.length === 0) return null
      const days = DAYS.map((d) => d.value).filter((d) => m.dayOfWeek.includes(d)).join(',')
      return `${p.sec} ${p.min} ${p.hour} * * ${days}`
    }
    case 'day-of-month': {
      const p = timesToCronParts(m.times)
      if (!p) return null
      const dom = Math.max(1, Math.min(31, Math.floor(m.dayOfMonth || 0)))
      return `${p.sec} ${p.min} ${p.hour} ${dom} * ?`
    }
    case 'custom':
      return m.raw.trim() || null
  }
}

/** Reverse: parse a Spring cron string into the structured model. */
function parseCron(expr: string): CronModel {
  const trimmed = (expr || '').trim()
  if (!trimmed) return { ...emptyModel }
  const parts = trimmed.split(/\s+/)
  const normalized = parts.length === 5 ? ['0', ...parts] : parts
  if (normalized.length !== 6) return { ...emptyModel, type: 'custom', raw: trimmed }

  const [sec, min, hour, dom, , dow] = normalized

  const everyMin = /^\*\/(\d+)$/.exec(min)
  if (sec === '0' && everyMin && (hour === '*' || hour === '*/1')) {
    return { ...emptyModel, type: 'every-minutes', intervalN: parseInt(everyMin[1], 10) }
  }
  const everyHour = /^\*\/(\d+)$/.exec(hour)
  if (sec === '0' && min === '0' && everyHour) {
    return { ...emptyModel, type: 'every-hours', intervalN: parseInt(everyHour[1], 10) }
  }

  const validMin = /^\d+$/.test(min)
  const validHourList = /^\d+(?:,\d+)*$/.test(hour)
  if (sec === '0' && validMin && validHourList) {
    const m = parseInt(min, 10)
    const times = hour
      .split(',')
      .map((h) => `${pad2(parseInt(h, 10))}:${pad2(m)}`)
    const dowUp = dow.toUpperCase()

    if (/^\d+$/.test(dom)) {
      return {
        ...emptyModel,
        type: 'day-of-month',
        times,
        dayOfMonth: parseInt(dom, 10),
      }
    }

    if (dom === '*' || dom === '?') {
      if (dowUp === '*' || dowUp === '?') {
        return { ...emptyModel, type: 'daily', times }
      }
      if (dowUp === 'MON-FRI' || dowUp === '1-5') {
        return { ...emptyModel, type: 'weekdays', times }
      }
      if (
        dowUp === 'SAT,SUN' ||
        dowUp === '6,0' ||
        dowUp === '0,6' ||
        dowUp === '6,7'
      ) {
        return { ...emptyModel, type: 'weekends', times }
      }
      const listed = dowUp.split(',').filter((d) => DAYS.some((dd) => dd.value === d))
      if (listed.length > 0) {
        return { ...emptyModel, type: 'days', times, dayOfWeek: listed }
      }
    }
  }

  return { ...emptyModel, type: 'custom', raw: trimmed }
}

interface CronBuilderProps {
  value: string
  onChange: (cron: string) => void
  disabled?: boolean
}

/**
 * Structured cron editor. Renders a type selector + the relevant detail
 * fields (interval N, time list, days, etc.) and serialises back to a
 * Spring cron string on every change so the saved value remains the canonical
 * `sec min hour dom month dow` expression. Unknown patterns drop into a raw
 * text input under the "Custom" type.
 */
export default function CronBuilder({ value, onChange, disabled }: CronBuilderProps) {
  // Parse the initial upstream value once for the seed model. We deliberately
  // don't depend on `value` here — once the form is mounted, the local model
  // is the source of truth while the operator is editing; the upstream value
  // is only re-pulled when it changes for a reason other than our own emit
  // (see the useEffect below).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initial = useMemo(() => parseCron(value), [])
  const [model, setModel] = useState<CronModel>(initial)
  const [timesInput, setTimesInput] = useState<string>(initial.times.join(', '))

  // Track the last value we emitted so we can ignore the echo when the parent
  // hands the same string back to us. Without this, a transient invalid state
  // during editing (e.g. emptying the times input on a "daily" schedule)
  // emits `''`, which `parseCron('')` would otherwise re-classify as Custom
  // and snap the type selector back. Now we only re-parse when the parent
  // sends us a string we didn't produce — i.e. a real external change like
  // a refetch or tab switch.
  const lastEmitted = useRef<string>(buildCron(initial) ?? '')

  useEffect(() => {
    if (value === lastEmitted.current) return
    const next = parseCron(value)
    lastEmitted.current = buildCron(next) ?? ''
    setModel(next)
    setTimesInput(next.times.join(', '))
  }, [value])

  const update = (patch: Partial<CronModel>) => {
    const next = { ...model, ...patch }
    setModel(next)
    const built = buildCron(next) ?? ''
    lastEmitted.current = built
    onChange(built)
  }

  const onTimesChange = (raw: string) => {
    setTimesInput(raw)
    const { times } = normalizeTimes(raw)
    update({ times })
  }

  const onDayToggle = (day: string) => {
    const set = new Set(model.dayOfWeek)
    if (set.has(day)) set.delete(day)
    else set.add(day)
    update({ dayOfWeek: [...set] })
  }

  const built = buildCron(model)
  const description = built ? describeCronExpression(built) : null
  const timesValidation = normalizeTimes(timesInput)
  const needsTimes = model.type !== 'every-minutes'
    && model.type !== 'every-hours'
    && model.type !== 'custom'

  return (
    <Stack spacing={1.25}>
      <FormControl size="small" fullWidth disabled={disabled}>
        <InputLabel id={`cron-type-label`}>Schedule type</InputLabel>
        <Select
          labelId="cron-type-label"
          label="Schedule type"
          value={model.type}
          onChange={(e) => update({ type: e.target.value as CronType })}
        >
          {TYPE_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {(model.type === 'every-minutes' || model.type === 'every-hours') && (
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="body2" color="text.secondary">
            Every
          </Typography>
          <TextField
            size="small"
            type="number"
            inputProps={{
              min: 1,
              max: model.type === 'every-minutes' ? 59 : 23,
            }}
            value={model.intervalN}
            onChange={(e) =>
              update({ intervalN: Math.max(1, parseInt(e.target.value, 10) || 1) })
            }
            disabled={disabled}
            sx={{ width: 96 }}
          />
          <Typography variant="body2" color="text.secondary">
            {model.type === 'every-minutes' ? 'minutes' : 'hours'}
          </Typography>
        </Stack>
      )}

      {needsTimes && (
        <TextField
          size="small"
          fullWidth
          label="Time(s) — comma separated, HH:MM"
          placeholder="e.g. 18:00, 22:00"
          value={timesInput}
          onChange={(e) => onTimesChange(e.target.value)}
          disabled={disabled}
          error={
            timesInput.trim() !== '' &&
            (!timesValidation.valid || !timesToCronParts(timesValidation.times))
          }
          helperText={
            timesInput.trim() === ''
              ? 'At least one time, e.g. 18:00, 22:00'
              : !timesValidation.valid
              ? 'Use HH:MM format'
              : !timesToCronParts(timesValidation.times)
              ? 'All times must share the same minute (e.g. 18:00 and 22:00, but not 18:30 and 22:00)'
              : ' '
          }
        />
      )}

      {model.type === 'days' && (
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Days
          </Typography>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {DAYS.map((d) => {
              const active = model.dayOfWeek.includes(d.value)
              return (
                <Chip
                  key={d.value}
                  label={d.label}
                  size="small"
                  color={active ? 'primary' : 'default'}
                  variant={active ? 'filled' : 'outlined'}
                  onClick={disabled ? undefined : () => onDayToggle(d.value)}
                />
              )
            })}
          </Stack>
        </Box>
      )}

      {model.type === 'day-of-month' && (
        <TextField
          size="small"
          type="number"
          label="Day of month"
          inputProps={{ min: 1, max: 31 }}
          value={model.dayOfMonth}
          onChange={(e) =>
            update({ dayOfMonth: Math.max(1, parseInt(e.target.value, 10) || 1) })
          }
          disabled={disabled}
          sx={{ width: 160 }}
        />
      )}

      {model.type === 'custom' && (
        <TextField
          size="small"
          fullWidth
          label="Spring cron expression"
          placeholder="sec min hour DoM month DoW"
          value={model.raw}
          onChange={(e) => update({ raw: e.target.value })}
          disabled={disabled}
          InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.85rem' } }}
        />
      )}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
          mt: 0.5,
        }}
      >
        <Chip
          label={built ?? '—'}
          size="small"
          variant="outlined"
          sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
        />
        {built && description && (
          <Typography variant="caption" color="text.secondary">
            {description}
          </Typography>
        )}
        {!built && (
          <Typography variant="caption" color="warning.main">
            Incomplete — value won't be saved until the form is valid.
          </Typography>
        )}
      </Box>
    </Stack>
  )
}
